import { DebugLogger } from '@affine/debug';
import type { GetWorkspaceConfigQuery, InviteLink } from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { exhaustMap, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { WorkspaceShareSettingStore } from '../stores/share-setting';

type EnableAi = GetWorkspaceConfigQuery['workspace']['enableAi'];
type EnableUrlPreview =
  GetWorkspaceConfigQuery['workspace']['enableUrlPreview'];
// sl-xm1c.2.4: Workspace-Admin Toggle for the Custom Apps feature
type CustomAppsEnabled =
  GetWorkspaceConfigQuery['workspace']['customAppsEnabled'];

const logger = new DebugLogger('affine:workspace-permission');

export class WorkspaceShareSetting extends Entity {
  enableAi$ = new LiveData<EnableAi | null>(null);
  enableUrlPreview$ = new LiveData<EnableUrlPreview | null>(null);
  // sl-xm1c.2.4: Workspace-Admin Toggle for the Custom Apps feature
  customAppsEnabled$ = new LiveData<CustomAppsEnabled | null>(null);
  inviteLink$ = new LiveData<InviteLink | null>(null);
  isLoading$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly store: WorkspaceShareSettingStore
  ) {
    super();
    this.revalidate();
  }

  revalidate = effect(
    exhaustMap(() => {
      return fromPromise(signal =>
        this.store.fetchWorkspaceConfig(
          this.workspaceService.workspace.id,
          signal
        )
      ).pipe(
        smartRetry(),
        tap(value => {
          if (value) {
            this.enableAi$.next(value.enableAi);
            this.enableUrlPreview$.next(value.enableUrlPreview);
            this.customAppsEnabled$.next(value.customAppsEnabled);
          }
        }),
        catchErrorInto(this.error$, error => {
          logger.error('Failed to fetch enableUrlPreview', error);
        }),
        onStart(() => this.isLoading$.setValue(true)),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  revalidateInviteLink = effect(
    exhaustMap(() =>
      fromPromise(signal =>
        this.store.fetchWorkspaceInviteLink(
          this.workspaceService.workspace.id,
          signal
        )
      ).pipe(
        tap(value => {
          this.inviteLink$.next(value);
        }),
        catchErrorInto(this.error$, error => {
          logger.error('Failed to fetch workspace invite link', error);
        })
      )
    )
  );

  async waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler — without this,
    // waitFor resolves immediately before the fetch starts.
    this.isLoading$.setValue(true);
    this.revalidate();
    await this.isLoading$.waitFor(isLoading => !isLoading, signal);
  }

  async setEnableUrlPreview(enableUrlPreview: EnableUrlPreview) {
    await this.store.updateWorkspaceEnableUrlPreview(
      this.workspaceService.workspace.id,
      enableUrlPreview
    );
    await this.waitForRevalidation();
  }

  async setEnableAi(enableAi: EnableAi) {
    await this.store.updateWorkspaceEnableAi(
      this.workspaceService.workspace.id,
      enableAi
    );
    await this.waitForRevalidation();
  }

  // sl-xm1c.2.4: Workspace-Admin Toggle for the Custom Apps feature
  async setCustomAppsEnabled(customAppsEnabled: CustomAppsEnabled) {
    await this.store.updateWorkspaceCustomAppsEnabled(
      this.workspaceService.workspace.id,
      customAppsEnabled
    );
    await this.waitForRevalidation();
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
    this.revalidateInviteLink.unsubscribe();
  }
}
